import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { DatabaseService } from '@motes-sdk/providers';

export interface ReviewByTour {
    key: string;
    userKey?: string;
    reviewKey?: string;
    appRating: number;
    tourRating: number;
    appCommentsPredefined: string[];
    tourCommentsPredefined: string[];
    appCommentsCustom: string;
    tourCommentsCustom: string;
    updatedOn: number;
    /** Origin – e.g. "legacy" (StoryHunt app), or app-provided for API reviews */
    source?: string;
    /** Rating scale min (default 0 for legacy) */
    ratingMin?: number;
    /** Rating scale max (default 4 for legacy; display as 1–5) */
    ratingMax?: number;
}

export interface ReviewsByTour {
    appRatingAvg: number;
    tourRatingAvg: number;
    appCommentsPredefinedTotal: number;
    tourCommentsPredefinedTotal: number;
    appCommentsCustomTotal: string;
    tourCommentsCustomTotal: string;
    reviews: ReviewByTour[];
}

@Injectable({
    providedIn: 'root'
})
export class ReviewByTourService {

    constructor(private db: DatabaseService) {}

    /**
     * Watch all reviews for a specific tour from the reviewsByTour collection
     */
    public watchReviewsByTour(tourKey: string): Observable<ReviewsByTour> {
        return this.db.list(`reviewsByTour/${tourKey}`).snapshotChanges().pipe(
            map(snapList => {
                console.log('snapList in watch', snapList);
                
                const reviews: ReviewByTour[] = snapList.map(snap => {
                    const data = snap.payload.val();
                    return {
                        key: snap.key,
                        userKey: snap.key,
                        reviewKey: `${tourKey}_${snap.key}`,
                        ...(data && typeof data === 'object' ? data : {})
                    } as ReviewByTour;
                });

                return this.calculateReviewsSummary(reviews);
            })
        );
    }

    /**
     * Get reviews for a specific tour (one-time fetch)
     */
    public async getReviewsByTour(tourKey: string): Promise<ReviewsByTour> {
        const snapshot = await this.db.database.ref(`reviewsByTour/${tourKey}`).once('value');
        const reviewsData = snapshot.val() || {};
        
        const reviews: ReviewByTour[] = Object.keys(reviewsData).map(key => ({
            key: key,
            userKey: key,
            reviewKey: `${tourKey}_${key}`,
            ...reviewsData[key]
        } as ReviewByTour));

        return this.calculateReviewsSummary(reviews);
    }

    /**
     * Watch reviews by date range for a specific tour
     */
    public watchReviewsByDateRange(tourKey: string, startTimestamp: number, endTimestamp: number): Observable<ReviewsByTour> {
        return this.watchReviewsByTour(tourKey).pipe(
            map(reviewsSummary => {
                const filteredReviews = reviewsSummary.reviews.filter(review => 
                    review.updatedOn >= startTimestamp && review.updatedOn <= endTimestamp
                );
                return this.calculateReviewsSummary(filteredReviews);
            })
        );
    }

    /**
     * Get reviews by date range for a specific tour (one-time fetch with server-side filtering)
     */
    public async getReviewsByDateRange(tourKey: string, startDate: Date, endDate: Date): Promise<ReviewsByTour> {
        const startTimestamp = startDate.getTime();
        const endTimestamp = endDate.getTime();
        
        // Query with server-side filtering using the updatedOn index
        const query = (ref) => ref
            .orderByChild('updatedOn')
            .startAt(startTimestamp)
            .endAt(endTimestamp);
            
        const snapshot = await firstValueFrom(
            this.db.list(`reviewsByTour/${tourKey}`, query).snapshotChanges().pipe(
                map(snapList => {
                    const reviews: ReviewByTour[] = snapList.map(snap => {
                        const data = snap.payload.val();
                        return {
                            key: snap.key,
                            userKey: snap.key,
                            reviewKey: `${tourKey}_${snap.key}`,
                            ...(data && typeof data === 'object' ? data : {})
                        } as ReviewByTour;
                    });
                    return this.calculateReviewsSummary(reviews);
                })
            )
        );

        return snapshot;
    }

    /**
     * Analyze all reviews for a tour to understand timestamp patterns
     */
    public async analyzeAllReviews(tourKey: string): Promise<void> {
        console.log('=== ANALYZING ALL REVIEWS FOR TOUR:', tourKey, '===');
        
        const snapshot = await this.db.database.ref(`reviewsByTour/${tourKey}`).once('value');
        const reviewsData = snapshot.val() || {};
        
        const reviewKeys = Object.keys(reviewsData);
        console.log('Total reviews found:', reviewKeys.length);
        
        // Analysis counters
        let totalReviews = 0;
        let reviewsWithTimestamps = 0;
        let reviewsIn1970 = 0;
        let reviewsBefore2020 = 0;
        let reviewsBefore2023 = 0;
        let reviewsInSeconds = 0;
        let reviewsInMilliseconds = 0;
        let oldestTimestamp = Number.MAX_SAFE_INTEGER;
        let newestTimestamp = 0;
        
        // Date thresholds
        const year2020 = new Date('2020-01-01').getTime();
        const year2023 = new Date('2023-01-01').getTime();
        const year1970 = new Date('1970-01-01').getTime();
        const year1971 = new Date('1971-01-01').getTime();
        
        reviewKeys.forEach(key => {
            const review = reviewsData[key];
            const updatedOn = review?.updatedOn;
            totalReviews++;
            
            if (updatedOn && typeof updatedOn === 'number') {
                reviewsWithTimestamps++;
                
                const dateFromTimestamp = new Date(updatedOn);
                const year = dateFromTimestamp.getFullYear();
                
                // Track oldest and newest
                if (updatedOn < oldestTimestamp) oldestTimestamp = updatedOn;
                if (updatedOn > newestTimestamp) newestTimestamp = updatedOn;
                
                // Check for 1970 dates
                if (year === 1970) {
                    reviewsIn1970++;
                }
                
                // Check for dates before 2020
                if (updatedOn < year2020) {
                    reviewsBefore2020++;
                }
                
                // Check for dates before 2023
                if (updatedOn < year2023) {
                    reviewsBefore2023++;
                }
                
                // Check if timestamp might be in seconds (typical range: 1000000000-2000000000)
                if (updatedOn > 1000000000 && updatedOn < 2000000000) {
                    reviewsInSeconds++;
                } else if (updatedOn > 1000000000000) { // Typical millisecond range
                    reviewsInMilliseconds++;
                }
            }
        });
        
        // Calculate how many would be fixed if we convert seconds to milliseconds
        let potentiallyFixableReviews = 0;
        let fixableBefore2020 = 0;
        let fixableBefore2023 = 0;
        
        reviewKeys.forEach(key => {
            const review = reviewsData[key];
            const updatedOn = review?.updatedOn;
            
            if (updatedOn && typeof updatedOn === 'number') {
                // Check if this looks like a seconds timestamp
                if (updatedOn > 1000000000 && updatedOn < 2000000000) {
                    const convertedTimestamp = updatedOn * 1000;
                    const convertedDate = new Date(convertedTimestamp);
                    const convertedYear = convertedDate.getFullYear();
                    
                    if (convertedYear > 1970) { // Would be a valid date after conversion
                        potentiallyFixableReviews++;
                        
                        if (convertedTimestamp < year2020) {
                            fixableBefore2020++;
                        }
                        
                        if (convertedTimestamp < year2023) {
                            fixableBefore2023++;
                        }
                    }
                }
            }
        });
        
        // Log comprehensive analysis
        console.log('\n=== TIMESTAMP ANALYSIS SUMMARY ===');
        console.log('Total reviews:', totalReviews);
        console.log('Reviews with timestamps:', reviewsWithTimestamps);
        console.log('Reviews in 1970:', reviewsIn1970);
        console.log('Reviews before 2020:', reviewsBefore2020);
        console.log('Reviews before 2023:', reviewsBefore2023);
        console.log('Reviews likely in seconds:', reviewsInSeconds);
        console.log('Reviews likely in milliseconds:', reviewsInMilliseconds);
        
        console.log('\n=== TIMESTAMP RANGES ===');
        console.log('Oldest timestamp:', oldestTimestamp, '->', new Date(oldestTimestamp));
        console.log('Newest timestamp:', newestTimestamp, '->', new Date(newestTimestamp));
        
        console.log('\n=== POTENTIAL FIXES ===');
        console.log('Reviews that could be fixed (seconds->milliseconds):', potentiallyFixableReviews);
        console.log('Fixed reviews would be before 2020:', fixableBefore2020);
        console.log('Fixed reviews would be before 2023:', fixableBefore2023);
        
        console.log('\n=== RECOMMENDATIONS ===');
        if (reviewsIn1970 > 0) {
            console.log('⚠️  Found', reviewsIn1970, 'reviews with 1970 dates - these need fixing!');
        }
        if (reviewsInSeconds > 0) {
            console.log('🔧 Found', reviewsInSeconds, 'reviews that appear to be in seconds - converting to milliseconds would fix them');
        }
        if (potentiallyFixableReviews > 0) {
            console.log('✅ Converting', potentiallyFixableReviews, 'timestamps from seconds to milliseconds would likely fix the date issues');
        }
        
        console.log('=== END ANALYSIS ===');
    }

    /**
     * Fix timestamps in the database by converting seconds to milliseconds
     */
    public async fixTimestampsInDatabase(tourKey: string): Promise<{ fixed: number, errors: string[] }> {
        console.log('=== FIXING TIMESTAMPS IN DATABASE FOR TOUR:', tourKey, '===');
        
        const snapshot = await this.db.database.ref(`reviewsByTour/${tourKey}`).once('value');
        const reviewsData = snapshot.val() || {};
        
        const reviewKeys = Object.keys(reviewsData);
        let fixedCount = 0;
        const errors: string[] = [];
        
        // Prepare batch updates
        const updates: { [key: string]: any } = {};
        
        reviewKeys.forEach(key => {
            const review = reviewsData[key];
            const updatedOn = review?.updatedOn;
            
            if (updatedOn && typeof updatedOn === 'number') {
                // Check if this looks like a seconds timestamp
                if (updatedOn > 1000000000 && updatedOn < 2000000000) {
                    const convertedTimestamp = updatedOn * 1000;
                    const convertedDate = new Date(convertedTimestamp);
                    const convertedYear = convertedDate.getFullYear();
                    
                    if (convertedYear > 1970) { // Would be a valid date after conversion
                        updates[`reviewsByTour/${tourKey}/${key}/updatedOn`] = convertedTimestamp;
                        fixedCount++;
                        console.log(`Fixing review ${key}: ${updatedOn} -> ${convertedTimestamp} (${convertedDate})`);
                    }
                }
            }
        });
        
        if (Object.keys(updates).length > 0) {
            try {
                await this.db.database.ref().update(updates);
                console.log(`✅ Successfully fixed ${fixedCount} timestamps in database`);
            } catch (error) {
                const errorMsg = `Failed to update database: ${error}`;
                console.error('❌', errorMsg);
                errors.push(errorMsg);
            }
        } else {
            console.log('ℹ️  No timestamps found that need fixing');
        }
        
        console.log('=== END FIX ===');
        return { fixed: fixedCount, errors };
    }

    /**
     * Analyze and optionally fix timestamps with user confirmation
     */
    public async analyzeAndFixTimestamps(tourKey: string, shouldFix: boolean = false): Promise<void> {
        console.log('=== ANALYZING AND FIXING TIMESTAMPS ===');
        
        // First, analyze the current state
        await this.analyzeAllReviews(tourKey);
        
        if (shouldFix) {
            console.log('\n🔧 Proceeding with timestamp fixes...');
            const result = await this.fixTimestampsInDatabase(tourKey);
            
            if (result.errors.length > 0) {
                console.error('❌ Errors occurred during fix:', result.errors);
            } else {
                console.log(`✅ Successfully fixed ${result.fixed} timestamps`);
            }
            
            // Re-analyze to show the results
            console.log('\n=== POST-FIX ANALYSIS ===');
            await this.analyzeAllReviews(tourKey);
        } else {
            console.log('\nℹ️  To fix the timestamps, call this method with shouldFix: true');
            console.log('Example: await this.reviewByTourService.analyzeAndFixTimestamps(tourKey, true);');
        }
        
        console.log('=== END ANALYZE AND FIX ===');
    }

    /**
     * Calculate summary statistics for reviews
     */
    private calculateReviewsSummary(reviews: ReviewByTour[]): ReviewsByTour {
        if (reviews.length === 0) {
            return {
                appRatingAvg: 0,
                tourRatingAvg: 0,
                appCommentsPredefinedTotal: 0,
                tourCommentsPredefinedTotal: 0,
                appCommentsCustomTotal: '',
                tourCommentsCustomTotal: '',
                reviews: reviews
            };
        }

        const appRatingSum = reviews.reduce((sum, review) => sum + (review.appRating || 0), 0);
        const tourRatingSum = reviews.reduce((sum, review) => sum + (review.tourRating || 0), 0);
        
        const appCommentsPredefinedTotal = reviews.reduce((sum, review) => sum + (review.appCommentsPredefined?.length || 0), 0);
        const tourCommentsPredefinedTotal = reviews.reduce((sum, review) => sum + (review.tourCommentsPredefined?.length || 0), 0);
        
        const appCommentsCustom = reviews
            .map(review => review.appCommentsCustom)
            .filter(comment => comment && comment.trim())
            .join(' | ');
        
        const tourCommentsCustom = reviews
            .map(review => review.tourCommentsCustom)
            .filter(comment => comment && comment.trim())
            .join(' | ');

        return {
            appRatingAvg: Math.round((appRatingSum / reviews.length) * 10) / 10,
            tourRatingAvg: Math.round((tourRatingSum / reviews.length) * 10) / 10,
            appCommentsPredefinedTotal,
            tourCommentsPredefinedTotal,
            appCommentsCustomTotal: appCommentsCustom,
            tourCommentsCustomTotal: tourCommentsCustom,
            reviews: reviews
        };
    }
}
